/**
 * Indian rupees numbers-to-words converter.
 *
 * Used as a helper hint below every money input in the Budget page (and anywhere else
 * that benefits from confirming a big number) — Indian users are used to reading amounts
 * both numerically and in words (lakh/crore) so the double-representation catches typos
 * like "50000" vs "500000" at a glance.
 *
 * Convention: uses the Indian numbering system (thousand, lakh, crore) — NOT million/billion.
 * Handles up to 99 crore (`9,99,99,99,999`). Larger numbers are returned as a plain
 * digit-grouped string with the "crore" suffix.
 *
 * Returns `""` for null/undefined/NaN/0/negative — callers can guard on truthiness.
 */

const ONES = [
  "",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const TENS = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

function under100(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o ? `${TENS[t]}-${ONES[o]}` : TENS[t];
}

function under1000(n) {
  if (n < 100) return under100(n);
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return rest ? `${ONES[h]} hundred ${under100(rest)}` : `${ONES[h]} hundred`;
}

export function numberToIndianWords(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n === 0) return "zero rupees";
  const crore = Math.floor(n / 1_00_00_000);
  const afterCrore = n % 1_00_00_000;
  const lakh = Math.floor(afterCrore / 1_00_000);
  const afterLakh = afterCrore % 1_00_000;
  const thousand = Math.floor(afterLakh / 1000);
  const rest = afterLakh % 1000;

  const parts = [];
  if (crore) parts.push(`${under1000(crore)} crore`);
  if (lakh) parts.push(`${under100(lakh)} lakh`);
  if (thousand) parts.push(`${under100(thousand)} thousand`);
  if (rest) parts.push(under1000(rest));

  const words = parts.join(" ").trim();
  return `${words} rupees`;
}
